import { MetricCard } from "./MetricCard";
import { ChartRenderer } from "./ChartRenderer";
import { DataTable } from "./DataTable";
import { Card } from "@chakra-ui/react";
import { FiAlertCircle } from "react-icons/fi";

interface VisualizationConfig {
  type: "card" | "line_chart" | "bar_chart" | "pie_chart" | "table" | "text";
  title: string;
  config: any;
}

interface ResultRendererProps {
  visualization: VisualizationConfig;
  results: any[];
  executionTime?: number;
}

export const ResultRenderer = ({
  visualization,
  results,
  executionTime,
}: ResultRendererProps) => {
  if (!results || results.length === 0) {
    return (
      <Card.Root className="p-6! bg-white! border border-gray-200! rounded-xl! shadow-sm">
        <div className="flex flex-col items-center justify-center text-center py-8">
          <div className="mb-4 p-3 bg-gray-100 rounded-full">
            <FiAlertCircle className="text-3xl text-gray-400" />
          </div>
          <h3 className="text-lg! font-semibold! mb-2! text-gray-700!">No Data Available</h3>
          <p className="text-gray-500! text-sm! max-w-md">
            The query returned no results. Try adjusting the date range or search terms.
          </p>
        </div>
        {executionTime !== undefined && (
          <div className="mt-4 pt-4 border-t border-gray-200 text-xs! text-gray-400! text-center font-mono!">
            Executed in {executionTime}ms
          </div>
        )}
      </Card.Root>
    );
  }

  const renderVisualization = () => {
    switch (visualization.type) {
      case "card":
        // For single metric display
        if (results.length > 0) {
          const firstResult = results[0];
          const value = Object.values(firstResult)[0];
          return (
            <MetricCard
              title={visualization.title}
              value={value as string | number}
              config={visualization.config}
            />
          );
        }
        return null;

      case "line_chart":
      case "bar_chart":
      case "pie_chart":
        return (
          <ChartRenderer
            type={visualization.type}
            title={visualization.title}
            data={results}
            config={visualization.config}
          />
        );

      case "table":
        return <DataTable title={visualization.title} data={results} />;

      case "text":
        // For textual insights or explanations
        return (
          <Card.Root className="p-6!">
            <h3 className="text-lg! font-semibold! mb-4!">{visualization.title}</h3>
            <div className="text-gray-700! space-y-2!">
              {results.map((item, index) => (
                <p key={index}>{JSON.stringify(item, null, 2)}</p>
              ))}
            </div>
          </Card.Root>
        );

      default:
        return <DataTable title={visualization.title} data={results} />;
    }
  };

  return (
    <div className="space-y-2">
      {renderVisualization()}
      {executionTime !== undefined && (
        <p className="text-xs! text-gray-500! text-right!">
          Executed in {executionTime}ms
        </p>
      )}
    </div>
  );
};
